import React, { useState } from 'react';
import { 
  PhotoIcon, 
  VideoCameraIcon, 
  DocumentIcon, 
  MagnifyingGlassIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';
import { Body } from '../ui/Typography';
import { ObservationMedia } from '../../types/api';
import { useFileValidation } from '../../hooks/api';

interface MediaGalleryProps {
  media: ObservationMedia[];
  showCaptions?: boolean;
  maxItemsToShow?: number;
  size?: 'sm' | 'md' | 'lg';
  onMediaClick?: (media: ObservationMedia, index: number) => void;
}

const MediaGallery: React.FC<MediaGalleryProps> = ({
  media,
  showCaptions = true,
  maxItemsToShow,
  size = 'md',
  onMediaClick,
}) => {
  const { formatFileSize } = useFileValidation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const displayedMedia = maxItemsToShow ? media.slice(0, maxItemsToShow) : media;
  const remainingCount = maxItemsToShow ? Math.max(0, media.length - maxItemsToShow) : 0;
  
  const handleMediaClick = (mediaItem: ObservationMedia, index: number) => {
    if (onMediaClick) {
      onMediaClick(mediaItem, index);
    } else {
      // Default behavior: open lightbox for images and videos
      if (mediaItem.media_type === 'image' || mediaItem.media_type === 'video') {
        setCurrentIndex(index);
        setLightboxOpen(true);
      }
    }
  };
  
  const nextMedia = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
  };
  
  const prevMedia = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  };
  
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-16 h-16';
      case 'md':
        return 'w-24 h-24';
      case 'lg':
        return 'w-32 h-32';
      default:
        return 'w-24 h-24';
    }
  };
  
  const getMediaIcon = (mediaType: string) => {
    const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    
    switch (mediaType) {
      case 'image':
        return <PhotoIcon className={`${iconSize} text-green-500`} />;
      case 'video':
        return <VideoCameraIcon className={`${iconSize} text-blue-500`} />;
      default:
        return <DocumentIcon className={`${iconSize} text-gray-500`} />;
    }
  };
  
  if (media.length === 0) {
    return (
      <div className="text-center py-4">
        <PhotoIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
        <Body className="text-sm text-gray-500">No media attached</Body>
      </div>
    );
  }
  
  return (
    <>
      <div className="space-y-2">
        {/* Media Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {displayedMedia.map((mediaItem, index) => (
            <MediaThumbnail
              key={mediaItem.id}
              media={mediaItem}
              index={index}
              size={size}
              showCaption={showCaptions}
              onClick={() => handleMediaClick(mediaItem, index)}
            />
          ))}
          
          {/* Show More Button */}
          {remainingCount > 0 && (
            <div
              className={`
                ${getSizeClasses()} 
                bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 
                flex flex-col items-center justify-center cursor-pointer
                hover:bg-gray-200 transition-colors
              `}
              onClick={() => handleMediaClick(media[maxItemsToShow!], maxItemsToShow!)}
            >
              <MagnifyingGlassIcon className="w-6 h-6 text-gray-400 mb-1" />
              <Body className="text-xs text-gray-500 text-center">
                +{remainingCount} more
              </Body>
            </div>
          )}
        </div>
        
        {/* Media Count */}
        {media.length > 0 && (
          <Body className="text-xs text-gray-500">
            {media.length} file{media.length !== 1 ? 's' : ''} attached
          </Body>
        )}
      </div>
      
      {/* Lightbox */}
      {lightboxOpen && (
        <MediaLightbox
          media={media}
          currentIndex={currentIndex}
          onClose={() => setLightboxOpen(false)}
          onNext={nextMedia}
          onPrevious={prevMedia}
        />
      )}
    </>
  );
};

interface MediaThumbnailProps {
  media: ObservationMedia;
  index: number;
  size: 'sm' | 'md' | 'lg';
  showCaption: boolean;
  onClick: () => void;
}

const MediaThumbnail: React.FC<MediaThumbnailProps> = ({
  media,
  size,
  showCaption,
  onClick,
}) => {
  const { formatFileSize } = useFileValidation();
  
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-16 h-16';
      case 'md':
        return 'w-24 h-24';
      case 'lg':
        return 'w-32 h-32';
      default:
        return 'w-24 h-24';
    }
  };
  
  const getMediaIcon = (mediaType: string) => {
    const iconSize = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
    
    switch (mediaType) {
      case 'image':
        return <PhotoIcon className={`${iconSize} text-green-500`} />;
      case 'video':
        return <VideoCameraIcon className={`${iconSize} text-blue-500`} />;
      default:
        return <DocumentIcon className={`${iconSize} text-gray-500`} />;
    }
  };
  
  return (
    <div className="group">
      <div
        className={`
          ${getSizeClasses()} 
          relative bg-gray-100 rounded-lg overflow-hidden cursor-pointer
          hover:shadow-md transition-shadow border border-gray-200
        `}
        onClick={onClick}
      >
        {/* Media Preview */}
        {media.media_type === 'image' && media.url ? (
          <img
            src={media.url}
            alt={media.caption || media.file_name}
            className="w-full h-full object-cover"
          />
        ) : media.media_type === 'video' && media.url ? (
          <div className="w-full h-full relative">
            <video
              src={media.url}
              className="w-full h-full object-cover"
              muted
            />
            {/* Video Play Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center">
              <div className="w-8 h-8 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                <div className="w-0 h-0 border-l-[6px] border-l-gray-700 border-y-[4px] border-y-transparent ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            {getMediaIcon(media.media_type)}
            <Body className="text-xs text-gray-500 mt-1 text-center px-1 truncate">
              {media.file_name.split('.').pop()?.toUpperCase()}
            </Body>
          </div>
        )}
        
        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
          <MagnifyingGlassIcon className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
      
      {/* Caption */}
      {showCaption && (
        <div className="mt-1 space-y-1">
          <Body className="text-xs text-gray-700 truncate">
            {media.caption || media.file_name}
          </Body>
          <Body className="text-xs text-gray-500">
            {formatFileSize(media.file_size)}
          </Body>
        </div>
      )}
    </div>
  );
};

interface MediaLightboxProps {
  media: ObservationMedia[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrevious: () => void;
}

const MediaLightbox: React.FC<MediaLightboxProps> = ({
  media,
  currentIndex,
  onClose,
  onNext,
  onPrevious,
}) => {
  const currentMedia = media[currentIndex];
  const { formatFileSize } = useFileValidation();
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        onPrevious();
        break;
      case 'ArrowRight':
        onNext();
        break;
    }
  };
  
  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <div className="max-w-5xl max-h-full w-full h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white">
          <div className="flex items-center space-x-4">
            <Body className="text-sm">
              {currentIndex + 1} of {media.length}
            </Body>
            <Body className="text-sm">
              {currentMedia.caption || currentMedia.file_name}
            </Body>
            <Body className="text-xs text-gray-300">
              {formatFileSize(currentMedia.file_size)}
            </Body>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white hover:bg-opacity-20"
          >
            <XMarkIcon className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Media Content */}
        <div className="flex-1 flex items-center justify-center p-4 relative">
          {/* Previous Button */}
          {media.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onPrevious();
              }}
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20 z-10"
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </Button>
          )}
          
          {/* Media Display */}
          <div className="max-w-full max-h-full">
            {currentMedia.media_type === 'image' && currentMedia.url ? (
              <img
                src={currentMedia.url}
                alt={currentMedia.caption || currentMedia.file_name}
                className="max-w-full max-h-full object-contain"
                onClick={(e) => e.stopPropagation()}
              />
            ) : currentMedia.media_type === 'video' && currentMedia.url ? (
              <video
                src={currentMedia.url}
                controls
                className="max-w-full max-h-full"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="bg-white rounded-lg p-8 text-center">
                <DocumentIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <Body className="text-gray-700 mb-2">
                  {currentMedia.file_name}
                </Body>
                <Body className="text-sm text-gray-500">
                  {formatFileSize(currentMedia.file_size)}
                </Body>
              </div>
            )}
          </div>
          
          {/* Next Button */}
          {media.length > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white hover:bg-white hover:bg-opacity-20 z-10"
            >
              <ArrowRightIcon className="w-6 h-6" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MediaGallery;
